import React from 'react';
import { ArrowUpFromLine, ArrowDownToLine } from 'lucide-react';
import { useLanguage, getTranslation } from '../../hooks/useLanguage';

/**
 * Segmented Export / Import selector shown above the upload card.
 *
 * The selected kind determines which OCR extraction profile the backend runs
 * and which declaration shape the rest of the workflow builds, so it must be
 * chosen before processing starts. Once processing has begun (or data has been
 * extracted) the choice is locked to keep the workflow internally consistent.
 */
export const DeclarationKindToggle = React.memo(({ kind, onChange, disabled = false }) => {
  const [isSpanish] = useLanguage();
  const activeKind = kind === 'import' ? 'import' : 'export';

  const options = [
    { value: 'export', label: getTranslation('export', isSpanish), Icon: ArrowUpFromLine },
    { value: 'import', label: getTranslation('import', isSpanish), Icon: ArrowDownToLine },
  ];

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-gray-700">
          {getTranslation('declarationType', isSpanish)}
        </span>
        {disabled && (
          <span className="text-xs text-gray-400">
            {getTranslation('declarationTypeLockedHint', isSpanish)}
          </span>
        )}
      </div>
      <div
        role="radiogroup"
        aria-label={getTranslation('declarationType', isSpanish)}
        className="inline-flex w-fit rounded-full bg-gray-100 p-1"
      >
        {options.map(({ value, label, Icon }) => {
          const isActive = activeKind === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={isActive}
              disabled={disabled}
              onClick={() => !disabled && !isActive && onChange?.(value)}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors disabled:cursor-not-allowed ${
                isActive
                  ? 'bg-default text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-800 disabled:opacity-60'
              }`}
            >
              <Icon className={`size-4`} />
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
});

DeclarationKindToggle.displayName = 'DeclarationKindToggle';

export default DeclarationKindToggle;
